import pick from 'lodash/pick';
import {
    AudioFeaturesComparisonKeys,
    ComparisonData,
    comparisonFeatureList,
} from './comparisonData';

// This is the filtered data chosen to display to the user
export interface AudioFeatureResults {
    pitchMedian: number;
    pitchRange: number;
    pauseRate: number;
    speechRate: number;
    pausesList: Array<Array<number>>;
}
// This is the raw api data
export interface AudioFeaturesApiResults {
    pitchMedian: number;
    pitchMax: number;
    pitchMin: number;
    pauseRate: number;
    speechRate: number;
    pausesList: Array<Array<number>>;
}

export type AudioFeatureResultsKeys = keyof AudioFeatureResults;
export type AudioFeatureResultsAPIKeys = keyof AudioFeaturesApiResults;

export const emptyUserResultData = {
    pitchMedian: 0,
    pitchRange: 0,
    pauseRate: 0,
    speechRate: 0,
    pausesList: [[0, 0]],
};

export const featureStringMap: {
    [key: string]: string;
} = {
    pitchMedian: 'Pitch Median',
    pitchRange: 'Pitch Range',
    speechRate: 'Speech Rate',
    pauseRate: 'Pause Rate',
};

export type AudioFeatureKey = keyof AudioFeatureResults;

export const graphFeatureDescription: {
    [key: string]: {
        name: string;
        graphType: string;
        description: string;
        units: string;
    };
} = {
    pitchMedian: {
        name: 'Pitch Median',
        graphType: 'bar',
        units: 'Hz',
        description:
            'This measures the average vibratory rates of the vocal folds. The average pitch for males is 100 Hz-150 Hz and for females is 180 Hz-250 Hz.',
    },
    pitchRange: {
        name: 'Pitch Range',
        graphType: 'bar',
        units: 'Hz',
        description:
            'This measures the difference between highest and lowest point of your voice. The values below are measured in hertz (Hz).',
    },
    pauseRate: {
        name: 'Pause Rate',
        graphType: 'bar',
        units: '',
        description:
            'This measures the pause rate which is calculated by dividing the duration by the total number of pauses.',
    },
    speechRate: {
        name: 'Speech Rate',
        graphType: 'bar',
        units: 'Words Per Second',
        description:
            'This measures the words per second in your recording. This chart compares how fast you speak compared to others.',
    },
    pausesList: {
        name: 'Pauses List',
        graphType: 'line',
        units: 'seconds',
        description:
            'This is the timeline of your first recording. The coloured spaces indicate the pauses within your speech.',
    },
};

/**
 * Rounds results to 2 decimals
 * @param {*} results - Object that can have pitchMedian, pitchRange, pauseRate, and/or speechRate
 * @returns pitchMedian, pitchRange, pauseRate, speechRate that is formatted
 */
export const formatResponseResults = (results: any) => {
    const pitchMinValue = parseFloat(
        parseFloat(results?.pitchMin ?? '0').toFixed(2)
    );
    const pitchMaxValue = parseFloat(
        parseFloat(results?.pitchMax ?? '0').toFixed(2)
    );

    return {
        pitchMedian: Math.max(
            0,
            parseFloat(parseFloat(results?.pitchMedian ?? '0').toFixed(2))
        ),
        pauseRate: Math.max(
            0,
            parseFloat(parseFloat(results?.pauseRate ?? '0').toFixed(2))
        ),
        speechRate: Math.max(
            0,
            parseFloat(parseFloat(results?.speechRate ?? '0').toFixed(2))
        ),
        pitchRange: parseFloat(
            Number(pitchMaxValue - pitchMinValue).toFixed(2)
        ),
        pausesList: results?.pausesList,
    };
};

/**
 * Gets the similarity percentage of two numbers by taking the difference
 * @param {number} a - A number
 * @param {number} b - A number
 * @returns similarity percentage
 */
const similarityPercentage = (a: number, b: number) => {
    const differencePercentage = 100 * Math.abs((a - b) / ((a + b) / 2 || 1));
    return Math.abs(100 - differencePercentage);
};

/**
 * Gets the similarity percentage of each feature and finds the average percentage
 * @param {AudioFeatureResults} results - the user's results
 * @param {Partial<AudioFeatureResults>} compareResults - single set of results to compare user to
 * @returns average of all the similarity percentage of each feature
 */
export const featureSimilarityPercentage = (
    results: AudioFeatureResults,
    compareResults: AudioFeatureResults
) => {
    const percentages = comparisonFeatureList.map(
        (key: AudioFeaturesComparisonKeys) => {
            const val = similarityPercentage(results[key], compareResults[key]);
            return val;
        }
    );

    const totalFeatures = Object.keys(compareResults).length;

    const average =
        percentages.reduce((partialSum, a) => partialSum + a, 0) /
        totalFeatures;
    return average;
};

/**
 * Compares the user's results with the comparison data to find the most similar person
 * @param {AudioFeatureResults} results - the user's results
 * @param { Array<ComparisonData>} compareResults - all of the comparison data
 * @returns The name and average similarity percentage of the most similar person that matches the user
 */
export const chooseMostSimilarPerson = (
    results: AudioFeatureResults,
    comparisonData: Array<ComparisonData>
) => {
    let mostSimilar = { name: comparisonData[0].name, val: 0 };

    const resultKeys = Object.keys(results);

    comparisonData.map((comparison) => {
        const comparisonFeatureData = pick(
            comparison,
            ...resultKeys
        ) as AudioFeatureResults;

        const percentage = featureSimilarityPercentage(
            results,
            comparisonFeatureData
        );
        if (mostSimilar.val < percentage) {
            mostSimilar = { name: comparison.name, val: percentage };
        }
    });

    const roundedVal = Math.round(mostSimilar.val * 100) / 100;
    return { ...mostSimilar, val: roundedVal };
};

/**
 * Checks for any failures in the results which will display as -1
 * @param {AudioFeatureResults} userData - the results
 * @returns {boolean} - true if user results failed
 */
export const isFailedToGetUseResults = (userData: AudioFeatureResults) => {
    for (const result in userData) {
        if (userData[result as AudioFeatureResultsKeys] === 0) {
            return true;
        }
    }
    return false;
};
